/**
 * 子话题饼图组件
 * 遵循 Opinion 图表开发规范
 */
import React, { useMemo, useRef } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import type { TRequestPieBackType } from '@op/utils/requestUtil';
import { DBO_V } from '@op/utils/format';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import useLanguage from '@op/hooks/useLanguage';
import { getPercentageInfo } from '@op/utils';
import { chartAllColors } from '@op/constants';

// 颜色加深函数
const darkenColor = (hex: string, factor: number): string => {
  // 处理 hex 格式
  if (hex.startsWith('#')) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    const newR = Math.round(r * (1 - factor));
    const newG = Math.round(g * (1 - factor));
    const newB = Math.round(b * (1 - factor));

    return `rgb(${newR}, ${newG}, ${newB})`;
  }
  return hex;
};

interface ISubTopicPieChartProps {
  data?: TRequestPieBackType;
  height: number;
  selectedType?: string | null;
  onChartClick?: (params: any) => void;
  getDisplayName: (val: string) => string;
}

const SubTopicPieChart: React.FC<ISubTopicPieChartProps> = ({
  data,
  height,
  selectedType,
  onChartClick,
  getDisplayName
}) => {
  const { formatMessage } = useLanguage();
  const eChartsRef = useRef<any>(null);

  const option = useMemo(() => {
    if (!data || data.isEmpty) return {};

    const series = data.series || [];

    // 计算总量
   const total = data.totalSeries || 0;

    const pieData = series.map((item, index) => {
      const isSelected = selectedType === item.name;
      const baseColor = chartAllColors[index % chartAllColors.length];
      const value = item.value || 0;

      // 未选中时颜色变淡
      const displayColor =
        selectedType && !isSelected ? `${baseColor}50` : baseColor;

      return {
        name: item.name,
        value,
        itemStyle: {
          color: displayColor,
          borderColor: isSelected ? darkenColor(baseColor, 0.3) : 'transparent',
          borderWidth: isSelected ? 3 : 0,
        },
      };
    });

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) =>
          formatChartParamsDate({
            params: [params],
            listTitleFunc: (item: any) => getDisplayName(item.name),
            tablesConfigFunc: (records: any) => [
              {
                showHeader: true,
                records: records.map((r: any) => ({
                  ...r,
                  value: `${DBO_V.reviews(
                    r.originValue,
                  )} (${params.percent.toFixed(2)}%)`,
                })),
                columns: [
                  { key: 'text', header: formatMessage({ id: 'Subtopic' }) },
                  {
                    key: 'value',
                    align: 'right',
                    weight: 'bold',
                    header: formatMessage({ id: 'Mentions' }),
                  },
                ],
              },
            ],
          }),
      },
      legend: {
        data: data.legend,
        formatter: (name: string) => getDisplayName(name),
      },
      series: [
        {
          type: 'pie',
          data: pieData,
          selectedMode: false,
          radius: ['40%', '70%'],
          label: {
            show: true,
            color: '#262832',
            alignTo: 'edge',
            edgeDistance: 10,
            formatter: (params: any) => {
              const displayName = getDisplayName(params.name);
              const percent = getPercentageInfo(params.value, total, 2);
              const percentText = params.value
                ? `${DBO_V.reviews(params.value)}(${percent})`
                : '-';
              return `{value| ${displayName}}: {a|${percentText}}\n{hrLine|}\n{a|}`;
            },
            rich: {
              value: {
                lineHeight: 20,
                align: 'center',
              },
              a: {
                lineHeight: 20,
                align: 'center',
              },
              hrLine: {
                borderColor: 'auto',
                width: '105%',
                borderWidth: 0.5,
                height: 0.5,
              },
              per: {
                padding: [4, 0],
              },
            },
          },
          labelLayout: (params: any) => {
            const isLeft =
              params.labelRect.x <
              eChartsRef?.current?.getEchartsInstance?.()?.getWidth() / 2;
            const points = params.labelLinePoints;
            points[2][0] = isLeft
              ? params.labelRect.x
              : params.labelRect.x + params.labelRect.width;
            return {
              labelLinePoints: points,
            };
          },
          labelLine: {
            show: true,
            length: 15,
            length2: 10,
          },
          emphasis: {
            focus: 'self',
            label: {
              opacity: 0.2,
            },
            labelLine: {
              lineStyle: {
                opacity: 0.2,
              },
            },
          },
          blur: {
            label: { opacity: 0.2 },
            itemStyle: {
              opacity: 0.2,
            },
            labelLine: {
              lineStyle: {
                opacity: 0.2,
              },
            },
          },
        },
      ],
    };
  }, [data, selectedType, getDisplayName, formatMessage]);

  const eventsDict = useMemo(() => {
    const events: Record<string, Function> = {};
    if (onChartClick) {
      events.click = onChartClick;
    }
    return events;
  }, [onChartClick]);

  return (
    <EChartCommon
      ref={eChartsRef}
      options={option}
      height={height}
      optionsProps={{ type: 'pie', pieLabelAlignTo: 'edge' }}
      eventsDict={eventsDict}
    />
  );
};

export default SubTopicPieChart;
